import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';

import AppShell from './components/ui/AppShell';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Production from './pages/Production';
import ProductionNext from './pages/ProductionNext';
import ProductionAnalytics from './pages/ProductionAnalytics';
import ScaleLive from './pages/ScaleLive';
import Users from './pages/Users';
import MyIncome from './pages/MyIncome';
import OzonSplit from './pages/OzonSplit';
import ReturnsReceiving from './pages/ReturnsReceiving';
import Fulfillment from './pages/Fulfillment';
import FulfillmentPicking from './pages/FulfillmentPicking';
import FulfillmentPickingJob from './pages/FulfillmentPickingJob';
import FulfillmentLabelingJobs from './pages/FulfillmentLabelingJobs';
import FulfillmentLabelingJob from './pages/FulfillmentLabelingJob';
import FulfillmentLoading from './pages/FulfillmentLoading';
import FulfillmentDashboard from './pages/FulfillmentDashboard';
import OzonFulfillment from './pages/OzonFulfillment';
import { AnalyticsFinance, AnalyticsAdvert, AnalyticsDaily, AnalyticsSales, AnalyticsPivot, AnalyticsSupplies, PurchasingDashboard, AiAnalyst, AnalyticsSearch, ProductDetails, AiApprovals, Competitors, AiEffectiveness, AiV2, StocksCost, AnalyticsPnl, SalesPlans, SalesPlanCreate, SalesPlanView, SupplyPlan, FboSupplyPlans, ForecastLab, ReviewsAlarm } from './pages/analytics';
import Products from './pages/Products';
import { CoversBinding, MsProducts, MpAccounts, Installations, ManualRun, DpdCalculator, CronMonitor, AiSafety, PrintAgents, PrintSettings } from './pages/settings';
import { getCurrentUser } from './store/slices/authSlice';
import { fetchProcesses } from './store/slices/processSlice';
import VersionChecker from './components/VersionChecker';
import { stationRouteAllowed } from './pages/productionStation.mjs';

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, user, loading } = useSelector((state) => state.auth);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(getCurrentUser());
    }
  }, [dispatch]);

  // Загружаем справочники после авторизации
  useEffect(() => {
    if (isAuthenticated && user && !(user.is_station === true || user.is_station === 1 || user.is_station === '1')) {
      dispatch(fetchProcesses());
    }
  }, [dispatch, isAuthenticated, user]);

  // Проверка прав доступа
  const hasPerm = (permKey) => {
    if (!isAuthenticated || !user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.[permKey] === true;
  };

  const isStation = user?.is_station === true || user?.is_station === 1 || user?.is_station === '1';

  // Определяем главную страницу в зависимости от прав
  const getHomePage = () => {
    if (isStation) return '/production/next';
    if (hasPerm('dashboard')) return '/';
    if (hasPerm('myIncome')) return '/my-income';
    if (hasPerm('production')) return '/production';
    if (hasPerm('fulfillmentRead')) return '/fulfillment';
    if (hasPerm('fulfillmentPicking')) return '/fulfillment/picking';
    if (hasPerm('fulfillmentLabeling')) return '/fulfillment/labeling';
    if (hasPerm('fulfillmentLoading')) return '/fulfillment/loading';
    if (hasPerm('fulfillmentDashboard')) return '/fulfillment/dashboard';
    return '/my-income'; // fallback
  };

  // Защищённый маршрут
  const ProtectedRoute = ({ children, permission, anyPermission }) => {
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (isStation && !stationRouteAllowed(location.pathname, user)) {
      return <Navigate to="/production/next" replace />;
    }
    if (permission && !hasPerm(permission)) return <Navigate to={getHomePage()} replace />;
    if (Array.isArray(anyPermission) && anyPermission.length && !anyPermission.some((key) => hasPerm(key))) {
      return <Navigate to={getHomePage()} replace />;
    }
    return children;
  };

  // Загрузка при проверке токена или когда авторизован но user ещё не загружен
  if ((loading && localStorage.getItem('token')) || (isAuthenticated && !user)) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    <VersionChecker />
    <Routes>
      <Route
        path="/login"
        element={!isAuthenticated ? <Login /> : <Navigate to={getHomePage()} replace />}
      />

      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell>
              <Routes>
                <Route path="/" element={
                  <ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>
                } />
                <Route path="/production" element={
                  <ProtectedRoute permission="production"><Production /></ProtectedRoute>
                } />
                <Route path="/production/next" element={
                  <ProtectedRoute anyPermission={['production', 'scale']}><ProductionNext /></ProtectedRoute>
                } />
                <Route path="/production/analytics" element={
                  <ProtectedRoute permission="productionAnalytics"><ProductionAnalytics /></ProtectedRoute>
                } />
                <Route path="/scale" element={
                  <ProtectedRoute permission="scale"><ScaleLive /></ProtectedRoute>
                } />
                <Route path="/analytics/finance" element={
                  <ProtectedRoute permission="analyticsFinance"><AnalyticsFinance /></ProtectedRoute>
                } />
                <Route path="/analytics/pnl" element={
                  <ProtectedRoute permission="analyticsPnl"><AnalyticsPnl /></ProtectedRoute>
                } />
                <Route path="/analytics/plans" element={
                  <ProtectedRoute permission="salesPlans"><SalesPlans /></ProtectedRoute>
                } />
                <Route path="/analytics/plans/new" element={
                  <ProtectedRoute permission="salesPlans"><SalesPlanCreate /></ProtectedRoute>
                } />
                <Route path="/analytics/plans/:id" element={
                  <ProtectedRoute permission="salesPlans"><SalesPlanView /></ProtectedRoute>
                } />
                <Route path="/analytics/supply-plan" element={
                  <ProtectedRoute permission="supplyPlanning"><SupplyPlan /></ProtectedRoute>
                } />
                <Route path="/analytics/fbo-supply-plans" element={
                  <ProtectedRoute permission="supplyPlanning"><FboSupplyPlans /></ProtectedRoute>
                } />
                <Route path="/analytics/forecast-lab" element={
                  <ProtectedRoute permission="forecastLab"><ForecastLab /></ProtectedRoute>
                } />
                <Route path="/analytics/daily" element={
                  <ProtectedRoute permission="analyticsFinance"><AnalyticsDaily /></ProtectedRoute>
                } />
                <Route path="/analytics/sales" element={
                  <ProtectedRoute permission="analyticsSales"><AnalyticsSales /></ProtectedRoute>
                } />
                <Route path="/analytics/pivot" element={
                  <ProtectedRoute permission="analyticsSales"><AnalyticsPivot /></ProtectedRoute>
                } />
                <Route path="/analytics/advert" element={
                  <ProtectedRoute permission="analyticsAdvert"><AnalyticsAdvert /></ProtectedRoute>
                } />
                <Route path="/analytics/supplies" element={
                  <ProtectedRoute permission="analyticsSupplies"><AnalyticsSupplies /></ProtectedRoute>
                } />
                <Route path="/analytics/purchasing" element={
                  <ProtectedRoute permission="purchasingDashboard"><PurchasingDashboard /></ProtectedRoute>
                } />
                <Route path="/analytics/ai" element={
                  <ProtectedRoute permission="aiAnalyst"><AiAnalyst /></ProtectedRoute>
                } />
                <Route path="/analytics/ai-v2" element={
                  <ProtectedRoute permission="aiAnalyst"><AiV2 /></ProtectedRoute>
                } />
                <Route path="/ai/approvals" element={
                  <ProtectedRoute permission="aiApprovals"><AiApprovals /></ProtectedRoute>
                } />
                <Route path="/ai/approvals/:id" element={
                  <ProtectedRoute permission="aiApprovals"><AiApprovals /></ProtectedRoute>
                } />
                <Route path="/analytics/search" element={
                  <ProtectedRoute permission="analyticsSearch"><AnalyticsSearch /></ProtectedRoute>
                } />
                <Route path="/analytics/reviews-alarm" element={
                  <ProtectedRoute permission="reviewsAlarm"><ReviewsAlarm /></ProtectedRoute>
                } />
                <Route path="/analytics/competitors" element={
                  <ProtectedRoute permission="aiAnalyst"><Competitors /></ProtectedRoute>
                } />
                <Route path="/analytics/ai-effectiveness" element={
                  <ProtectedRoute permission="aiEffectiveness"><AiEffectiveness /></ProtectedRoute>
                } />
                <Route path="/analytics/stocks" element={
                  <ProtectedRoute permission="products"><StocksCost /></ProtectedRoute>
                } />
                <Route path="/analytics/products/ms" element={
                  <ProtectedRoute permission="products"><MsProducts /></ProtectedRoute>
                } />
                <Route path="/analytics/products/details" element={
                  <ProtectedRoute permission="products"><ProductDetails /></ProtectedRoute>
                } />
                <Route path="/analytics/products" element={
                  <ProtectedRoute permission="products"><Products /></ProtectedRoute>
                } />
                <Route path="/settings/employees" element={
                  <ProtectedRoute permission="users"><Users /></ProtectedRoute>
                } />
                <Route path="/settings/covers-binding" element={
                  <ProtectedRoute permission="coversBinding"><CoversBinding /></ProtectedRoute>
                } />
                <Route path="/settings/ms-wb-binding" element={
                  <ProtectedRoute permission="products"><Navigate to="/analytics/products/ms" replace /></ProtectedRoute>
                } />
                <Route path="/settings/ms-products" element={
                  <ProtectedRoute permission="products"><Navigate to="/analytics/products/ms" replace /></ProtectedRoute>
                } />
                <Route path="/settings/mp-accounts" element={
                  <ProtectedRoute permission="mpAccounts"><MpAccounts /></ProtectedRoute>
                } />
                <Route path="/settings/installations" element={
                  <ProtectedRoute permission="settingsManual"><Installations /></ProtectedRoute>
                } />
                <Route path="/settings/manual" element={
                  <ProtectedRoute permission="settingsManual"><ManualRun /></ProtectedRoute>
                } />
                <Route path="/settings/dpd-calculator" element={
                  <ProtectedRoute permission="dpdCalculator"><DpdCalculator /></ProtectedRoute>
                } />
                <Route path="/settings/cron-monitor" element={
                  <ProtectedRoute permission="cronAdmin"><CronMonitor /></ProtectedRoute>
                } />
                <Route path="/settings/ai-safety" element={
                  <ProtectedRoute permission="aiAutonomyConfig"><AiSafety /></ProtectedRoute>
                } />
                <Route path="/settings/print-agents" element={
                  <ProtectedRoute permission="printAgents"><PrintAgents /></ProtectedRoute>
                } />
                <Route path="/settings/printing" element={
                  <ProtectedRoute permission="printQueue"><PrintSettings /></ProtectedRoute>
                } />
                <Route path="/my-income" element={
                  <ProtectedRoute permission="myIncome"><MyIncome /></ProtectedRoute>
                } />
                <Route path="/ozon/split" element={
                  <ProtectedRoute permission="ozonSplit"><OzonSplit /></ProtectedRoute>
                } />
                <Route path="/fulfillment" element={
                  <ProtectedRoute permission="fulfillmentRead"><Fulfillment /></ProtectedRoute>
                } />
                <Route path="/fulfillment/dashboard" element={
                  <ProtectedRoute permission="fulfillmentDashboard"><FulfillmentDashboard /></ProtectedRoute>
                } />
                <Route path="/fulfillment/ozon" element={
                  <ProtectedRoute permission="fulfillmentRead"><OzonFulfillment /></ProtectedRoute>
                } />
                <Route path="/fulfillment/picking" element={
                  <ProtectedRoute permission="fulfillmentPicking"><FulfillmentPicking /></ProtectedRoute>
                } />
                <Route path="/fulfillment/picking/:jobId" element={
                  <ProtectedRoute permission="fulfillmentPicking"><FulfillmentPickingJob /></ProtectedRoute>
                } />
                <Route path="/fulfillment/labeling" element={
                  <ProtectedRoute permission="fulfillmentLabeling"><FulfillmentLabelingJobs /></ProtectedRoute>
                } />
                <Route path="/fulfillment/labeling/:jobId" element={
                  <ProtectedRoute permission="fulfillmentLabeling"><FulfillmentLabelingJob /></ProtectedRoute>
                } />
                <Route path="/fulfillment/loading" element={
                  <ProtectedRoute permission="fulfillmentLoading"><FulfillmentLoading /></ProtectedRoute>
                } />
                <Route path="/returns/receiving" element={
                  <ProtectedRoute permission="returnsReceiving"><ReturnsReceiving /></ProtectedRoute>
                } />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}

export default App;
